// CoPilot session core (CR-20260619-001, M2-2).
//
// The frontend-owned (ADR-026) Conversation aggregate + the GLOBAL single-flight Run lock +
// Solution identity assignment + the Apply/Block state machine. All FE-only; nothing persists.
// Pure logic (no Angular) so the invariants are unit-tested directly (TC-U-F04/F05/F11/F12).
//
// ES5 note: no for..of / spread over Set/Map (project rule); arrays only.

import {
    Conversation,
    DivergenceState,
    Run,
    RunOutcome,
    Solution,
    SolutionContent,
    SolutionId,
    SolutionState,
    Tab,
    TabInputs,
    TabKind
} from './copilot-domain';

// ---- Global single-flight lock (FR-35/36, NFR-12; TC-U-F04) ----
//
// At most ONE Run in flight across ALL tabs. A 2nd RUN SKILL (any tab) cannot start while a
// Run is pending; releasing re-enables every tab.
export class SingleFlightLock {
    private _heldBy: string | null = null;

    get isHeld(): boolean {
        return this._heldBy !== null;
    }

    get holder(): string | null {
        return this._heldBy;
    }

    // Acquire for a run id; fails (returns false) if already held.
    tryAcquire(runId: string): boolean {
        if (this._heldBy !== null) {
            return false;
        }
        this._heldBy = runId;
        return true;
    }

    // Release only if this run holds it (a superseded run must not release a newer holder).
    release(runId: string): void {
        if (this._heldBy === runId) {
            this._heldBy = null;
        }
    }

    releaseAny(): void {
        this._heldBy = null;
    }
}

// ---- Solution identity + Apply/Block state machine ----

function freshState(): SolutionState {
    return { previewed: false, applied: false, blocked: false };
}

export function isBlocked(s: SolutionState): boolean {
    return s.blocked;
}

// Apply is refused on a blocked Solution (AC-96/97; FR-149/150).
export function canApply(s: SolutionState): boolean {
    return !s.blocked;
}

// Build Solutions from skill-authored content, assigning each a HOST identity = its slot index
// in the Run (FR-63/64; TC-U-F12). Two same-Name Solutions get DISTINCT ids; the Name is never
// used as a key. Identity is "<runId>#<index>".
export function buildSolutions(runId: string, contents: SolutionContent[]): Solution[] {
    return contents.map((content, index) => ({
        id: runId + '#' + index,
        content,
        state: freshState()
    }));
}

// Apply(id) within a tab (FR-86/87/88; TC-U-F11): the chosen Solution becomes Applied (and is itself
// never blocked); EVERY sibling becomes Applied=false + blocked=true. Auto-switches any previously-
// applied sibling, guaranteeing <=1 Applied per tab. Apply has NO grid effect (FR-91).
export function applySolution(solutions: Solution[], id: SolutionId): void {
    solutions.forEach(s => {
        if (s.id === id) {
            s.state.applied = true;
            s.state.blocked = false;
        } else {
            s.state.applied = false;
            s.state.blocked = true;
        }
    });
}

// un-Apply (FR-89; TC-U-F11): clears the Applied choice and unblocks the siblings it had blocked.
// Touches no legs (the caller does not un-Preview here).
export function unApplySolution(solutions: Solution[]): void {
    solutions.forEach(s => {
        s.state.applied = false;
        s.state.blocked = false;
    });
}

// Additive Apply (CR-20260619-001 MVP, Close tab): close Solutions are INDEPENDENT hedges (one hedge =
// one Solution), so applying one must NOT block the siblings. Toggles ONLY the targeted Solution; never
// blocks others. Relaxes "<=1 Applied per tab" to 0..N for close. Apply has NO grid effect (FR-91).
export function applySolutionAdditive(solutions: Solution[], id: SolutionId): void {
    solutions.forEach(s => {
        if (s.id === id) {
            s.state.applied = true;
            s.state.blocked = false;
        }
    });
}

// Additive un-Apply (Close tab): clears ONLY the targeted Solution; leaves the other applied hedges intact.
export function unApplySolutionAdditive(solutions: Solution[], id: SolutionId): void {
    solutions.forEach(s => {
        if (s.id === id) {
            s.state.applied = false;
            s.state.blocked = false;
        }
    });
}

// ---- Conversation aggregate + Run lifecycle ----

function emptyTab(kind: TabKind): Tab {
    return { kind, inputs: null, currentRun: null, solutions: [] };
}

// Outcomes the BE can emit (the rest — pending/cancelled — are FE-only). Mirrors
// CoPilotSolutionsReadyDto.Outcome.
export type BackendOutcome = 'succeeded' | 'empty' | 'terminalFailed' | 'preSendBlocked';

export class CoPilotSession {
    private readonly _lock = new SingleFlightLock();
    private _conversation: Conversation | null = null;
    private _panelInstanceId: string | null = null;

    // idFactory: client-mints Run/Conversation ids (e.g. getShortUUID); clock: epoch-ms source.
    // Both injected so the pure specs stay deterministic.
    constructor(
        private readonly _newId: () => string,
        private readonly _now: () => number
    ) {}

    get conversation(): Conversation | null {
        return this._conversation;
    }

    // CR-20260910-001 milestone 2: an opaque per-PANEL-INSTANCE id, for the CLOSE only.
    //
    // The Close's backend registry scopes supersede to (terminal, panel instance), so two Hedging
    // Grid panels on one terminal never kill each other's closes. Adjust's registry keys on the
    // terminal alone and is untouched by this — the id simply travels on the close command.
    //
    // A session lives exactly as long as its panel (one session per component, never replaced;
    // conversations restart INSIDE it), so a session-lifetime id IS a panel-instance id. Minted
    // lazily on first read, so constructing a session stays allocation-neutral for the
    // deterministic id factories the specs inject.
    get panelInstanceId(): string {
        if (this._panelInstanceId === null) {
            this._panelInstanceId = this._newId();
        }
        return this._panelInstanceId;
    }

    get isRunInFlight(): boolean {
        return this._lock.isHeld;
    }

    get inFlightRunId(): string | null {
        return this._lock.holder;
    }

    // Start a Conversation bound to one portfolio (FR-130). Re-arms divergence (FR-99/101/103).
    startConversation(portfolioId: string): Conversation {
        this._conversation = {
            id: this._newId(),
            portfolioId,
            divergence: 'armed',
            tabs: { select: emptyTab('select'), adjust: emptyTab('adjust'), close: emptyTab('close') }
        };
        return this._conversation;
    }

    tab(kind: TabKind): Tab | null {
        return this._conversation ? this._conversation.tabs[kind] : null;
    }

    setDivergence(state: DivergenceState): void {
        if (this._conversation) {
            this._conversation.divergence = state;
        }
    }

    // Start a Run on a tab (FR-35/139; TC-U-F04/F05). Returns null if the global lock is held
    // (another Run in flight). Otherwise: supersedes this tab's prior CURRENT Run, snapshots the
    // inputs, acquires the lock, and returns the new pending Run.
    startRun(kind: TabKind, inputs: TabInputs): Run | null {
        const tab = this.tab(kind);
        if (!tab) {
            return null;
        }
        const runId = this._newId();
        if (!this._lock.tryAcquire(runId)) {
            return null;   // single-flight: a Run is already pending
        }
        const run: Run = {
            id: runId,
            tabKind: kind,
            sentInputs: inputs,
            outcome: 'pending',
            attempts: 0,
            startedAt: this._now(),
            endedAt: null,
            failureDetail: null,
            notes: []
        };
        tab.currentRun = run;       // supersedes any prior CURRENT Run on this tab (TC-U-F05)
        return run;
    }

    // Is this run still the tab's CURRENT one AND the lock holder? Late/cancelled-run results
    // must be discarded (the run was superseded or cancelled). FR-47.
    isCurrent(runId: string): boolean {
        if (this._lock.holder !== runId) {
            return false;
        }
        const tab = this._tabOfRun(runId);
        return tab != null && tab.currentRun != null && tab.currentRun.id === runId;
    }

    // Apply a backend outcome to the in-flight Run (FR-56/57/48/32). Ignores results for a run
    // that is no longer current (late/dup discard — FR-47). On 'succeeded', builds Solutions
    // with host identities. Releases the global lock.
    completeRun(runId: string, outcome: BackendOutcome, contents: SolutionContent[], failureDetail: string | null,
                notes: string[] | null = null): boolean {
        if (!this.isCurrent(runId)) {
            return false;   // discarded
        }
        const tab = this._tabOfRun(runId);
        const run = tab.currentRun;
        run.outcome = outcome as RunOutcome;
        run.endedAt = this._now();
        run.failureDetail = failureDetail;
        // Defaulted rather than required: every OTHER caller of completeRun is a locally-generated
        // outcome (a Close with nothing to close, a cancelled run) that has no levels to drop.
        run.notes = notes || [];
        tab.solutions = outcome === 'succeeded' ? buildSolutions(runId, contents) : [];
        this._lock.release(runId);
        return true;
    }

    // Cancel the in-flight Run (FE-side; the BE pushes nothing — FR-43). Releases the lock so the
    // user can immediately RUN again without waiting out the timeout.
    cancelRun(runId: string): void {
        const tab = this._tabOfRun(runId);
        if (tab && tab.currentRun && tab.currentRun.id === runId) {
            tab.currentRun.outcome = 'cancelled';
            tab.currentRun.endedAt = this._now();
        }
        this._lock.release(runId);
    }

    // Re-run reset for a tab (FR-104..106): keep the questionnaire inputs; clear Solutions and the
    // CURRENT Run's results (Applied/Blocked live on the cleared Solutions). The caller un-Previews
    // any prior CoPilot legs first (M3) and recomputes divergence.
    resetTabForReRun(kind: TabKind): void {
        const tab = this.tab(kind);
        if (!tab) {
            return;
        }
        tab.solutions = [];
        tab.currentRun = null;
    }

    private _tabOfRun(runId: string): Tab | null {
        if (!this._conversation) {
            return null;
        }
        const tabs = this._conversation.tabs;
        const all = [tabs.select, tabs.adjust, tabs.close];
        let match: Tab | null = null;
        all.forEach(t => {
            if (t.currentRun && t.currentRun.id === runId) {
                match = t;
            }
        });
        return match;
    }
}
